import { doc, setDoc } from 'firebase/firestore';
import { db } from './firebase';
import { normalizePlate } from './wallets';

export async function sha256Hex(text) {
    const buf = await window.crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

export async function attestationToken(tenantId, policyId) {
    return (await sha256Hex((tenantId || 'igi_insurance') + '|' + policyId)).slice(0, 20);
}

export function securityKeyFromToken(token) {
    const hex = String(token || '0');
    let n = '';
    for (let i = 0; i < hex.length; i += 1) n += String(parseInt(hex[i], 16) % 10);
    return (n + '3184514257586070504').slice(0, 19);
}

export function attestationNumber(policy) {
    const raw = String(policy.policyNumber || policy.id || '000000').replace(/[^A-Z0-9]/gi, '').toUpperCase();
    const core = raw.length <= 10 ? raw.padStart(10, '0') : raw.slice(-10);
    return 'GM' + core;
}

export function formatCertDate(value, withTime) {
    if (!value) return '—';
    const d = value && typeof value.toDate === 'function' ? value.toDate() : new Date(value);
    if (Number.isNaN(d.getTime())) return '—';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    if (!withTime) return day + '-' + month + '-' + year;
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return day + '-' + month + '-' + year + ' ' + hh + ':' + mm;
}

export function plateOf(policy) {
    return (policy.vehicle && policy.vehicle.registrationNumber) || '—';
}

export function coverStamp(policy) {
    const s = String((policy && policy.status) || '').toUpperCase();
    const untilRaw = policy && policy.expiryDate;
    const until = untilRaw && typeof untilRaw.toDate === 'function' ? untilRaw.toDate() : (untilRaw ? new Date(untilRaw) : null);
    const expiredByDate = until && !Number.isNaN(until.getTime()) && until.getTime() < Date.now();
    if (s === 'EXPIRED' || s === 'LAPSED' || s === 'CANCELLED' || expiredByDate) {
        return { label: 'EXPIRED', kind: 'expired' };
    }
    if (s === 'ACTIVE' || s === 'APPROVED') return { label: 'VALID', kind: 'ok' };
    return { label: s || 'ISSUED', kind: 'wait' };
}

export function certificateTitle(policy) {
    const t = String((policy && (policy.policyType || policy.categoryName)) || '').toLowerCase();
    if (t.includes('life')) return 'LIFE INSURANCE CERTIFICATE';
    if (t.includes('medic')) return 'MEDICAL INSURANCE CERTIFICATE';
    if (t.includes('propert')) return 'PROPERTY INSURANCE CERTIFICATE';
    return 'MOTOR INSURANCE CERTIFICATE';
}

export function verifyUrl(token) {
    return window.location.origin + '/verify/' + token;
}

export function qrImageUrl(data) {
    return 'https://api.qrserver.com/v1/create-qr-code/?size=240x240&margin=8&data=' + encodeURIComponent(data);
}

export async function publishAttestation({ tenantId, companyName, logoUrl, customer, policy }) {
    const token = await attestationToken(tenantId || 'igi_insurance', policy.id);
    const payload = {
        token,
        tenantId: tenantId || 'igi_insurance',
        policyId: policy.id,
        companyName: companyName || '',
        logoUrl: logoUrl || '',
        holderName: (customer && customer.fullName) || '',
        nationalId: (customer && customer.nationalId) || '',
        phone: (customer && customer.phone) || '',
        policyNumber: policy.policyNumber || '',
        status: policy.status || '',
        cover: policy.productName || policy.coverageType || '',
        policyType: policy.policyType || '',
        validFrom: policy.effectiveDate || '',
        validTo: policy.expiryDate || '',
        issuedAt: policy.createdAt || policy.effectiveDate || '',
        plate: (policy.vehicle && policy.vehicle.registrationNumber) || '',
        make: (policy.vehicle && policy.vehicle.make) || '',
        model: (policy.vehicle && policy.vehicle.model) || '',
        year: (policy.vehicle && policy.vehicle.year) || '',
        vin: (policy.vehicle && (policy.vehicle.vin || policy.vehicle.VIN)) || '',
        vehicleUse: (policy.vehicle && policy.vehicle.use) || 'private',
        ecowasBrownCard: !!policy.ecowasBrownCard,
        attestationNo: attestationNumber(policy),
        securityKey: securityKeyFromToken(token)
    };
    await setDoc(doc(db, 'attestations', token), payload);
    const plateKey = normalizePlate(payload.plate);
    if (plateKey) {
        await setDoc(doc(db, 'plateIndex', plateKey), {
            plate: plateKey,
            plateDisplay: payload.plate || plateKey,
            token,
            tenantId: payload.tenantId,
            companyName: payload.companyName || '',
            holderName: payload.holderName || '',
            policyNumber: payload.policyNumber || '',
            status: payload.status || '',
            validFrom: payload.validFrom || '',
            validTo: payload.validTo || '',
            make: payload.make || '',
            model: payload.model || '',
            year: payload.year || '',
            policyId: payload.policyId,
            updatedAt: new Date().toISOString()
        });
    }
    return token;
}
